import { useCallback } from 'react';
import { useDialog } from '@gokrew/ui/context';
import type { InviteCredentials } from '@gokrew/ui/components/InviteSuccessDialog';
import { AddEmployeeForm } from '@/pages/employees/AddEmployeeForm';
import type {
  EmployeeCreatedEvent,
  HireCandidate,
  LinkAccountPrefill,
} from '@/types/employee';
import { useStartOnboardingWorkflow } from './useStartOnboardingWorkflow';

export interface AddEmployeeDialogOptions {
  title: string;
  /**
   * A hired applicant to prefill from; the save links the application, and
   * the job's title and department preselect Job Title and Department.
   */
  candidate?: HireCandidate;
  /** The workflow to preselect; see `AddEmployeeForm`. */
  defaultOnboardingTaskListId?: string | null;
  onInviteSuccess?: (credentials: InviteCredentials) => void;
  /** After the save, alongside the onboarding workflow start. */
  onCreated?: (created: EmployeeCreatedEvent) => void;
  /** Create the file on this administrator's login; see `AddEmployeeForm`. */
  linkAccount?: LinkAccountPrefill;
}

/**
 * Opens the Add employee form, from the Employees page or a hire in
 * recruiting alike. Once the employee is saved, the onboarding workflow
 * picked on the form starts with every task of its template, with no
 * separate "Start onboarding" step.
 */
export const useAddEmployeeDialog = () => {
  const { openDialog } = useDialog();
  const startOnboarding = useStartOnboardingWorkflow();

  return useCallback(
    ({
      title,
      candidate,
      defaultOnboardingTaskListId,
      onInviteSuccess,
      onCreated,
      linkAccount,
    }: AddEmployeeDialogOptions) =>
      openDialog({
        title,
        content: (
          <AddEmployeeForm
            candidate={candidate}
            defaultOnboardingTaskListId={defaultOnboardingTaskListId}
            onInviteSuccess={onInviteSuccess}
            linkAccount={linkAccount}
            onCreated={(created) => {
              onCreated?.(created);
              if (created.onboardingWorkflow) {
                startOnboarding({
                  employeeId: created.employeeId,
                  taskListId: created.onboardingWorkflow.id,
                  workflowName: created.onboardingWorkflow.name,
                  hasJoiningDate: created.hasJoiningDate,
                });
              }
            }}
          />
        ),
        containerClass: 'w-full md:w-[64rem] md:max-w-none',
      }),
    [openDialog, startOnboarding]
  );
};
